import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FlaskConical, MapPin, Phone, Mail, Globe, Instagram, Facebook, Droplets, Factory, Package, Tag, Lock, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { OwnershipBadges } from '../components/OwnershipBadges';

const API = "";

const ExtractorProfile = () => {
  const { extractorId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [extractor, setExtractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExtractor = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/api/extractors/${extractorId}`, { headers });
        setExtractor(response.data);
      } catch (err) {
        setError('Extractor not found');
      } finally {
        setLoading(false);
      }
    };
    fetchExtractor();
  }, [extractorId, token]);

  // Check if contact info is available (indicates user is approved dispensary)
  const hasContactInfo = extractor?.email || extractor?.phone;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !extractor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <FlaskConical className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Extractor Not Found</h2>
            <p className="text-muted-foreground mb-4">This extractor profile doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/canna-club')}>
              Back to Canna-Club
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/10 to-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-lg font-bold truncate">{extractor.business_name}</h1>
        </div>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative"
      >
        {extractor.image_url ? (
          <div className="aspect-video sm:aspect-[21/9] w-full overflow-hidden">
            <img
              src={extractor.image_url}
              alt={extractor.business_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('bg-gradient-to-br', 'from-purple-900/30', 'to-purple-600/10', 'flex', 'items-center', 'justify-center');
                const icon = document.createElement('div');
                icon.innerHTML = '<svg class="w-24 h-24 text-purple-500/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M10 2v6.31a1 1 0 0 1-.37.78l-3.26 2.6a1 1 0 0 0-.37.78V20c0 .55.45 1 1 1h10c.55 0 1-.45 1-1v-7.53c0-.3-.14-.58-.37-.78l-3.26-2.6a1 1 0 0 1-.37-.78V2"/></svg>';
                e.target.parentElement.appendChild(icon.firstChild);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        ) : (
          <div className="aspect-video sm:aspect-[21/9] w-full bg-gradient-to-br from-purple-900/30 to-purple-600/10 flex items-center justify-center relative">
            <FlaskConical className="w-24 h-24 text-purple-500/30" />
          </div>
        )}

        {/* Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                <FlaskConical size={12} className="mr-1" /> Extractor
              </Badge>
              {extractor.is_verified && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                  <CheckCircle size={12} className="mr-1" /> Verified
                </Badge>
              )}
              {extractor.is_featured && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  Featured
                </Badge>
              )}
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-1">{extractor.business_name}</h1>
            <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2">
              <MapPin size={14} /> {extractor.city}, Minnesota
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 space-y-4">
        
        {/* Ownership Badges */}
        {(extractor.women_owned || extractor.native_owned || extractor.bipoc_owned) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            <OwnershipBadges 
              womenOwned={extractor.women_owned} 
              nativeOwned={extractor.native_owned} 
              bipocOwned={extractor.bipoc_owned}
              size="default"
            />
          </motion.div>
        )}

        {/* Extraction Types */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-2"
        >
          {extractor.extraction_types?.split(',').map((type, idx) => (
            <Badge key={idx} variant="outline" className="px-3 py-1 border-purple-500/30 text-purple-400">
              <Droplets size={14} className="mr-1" /> {type.trim()}
            </Badge>
          ))}
        </motion.div>

        {/* Services */}
        {(extractor.accepts_wholesale || extractor.white_label) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="flex flex-wrap gap-2"
          >
            {extractor.accepts_wholesale && (
              <Badge variant="outline" className="px-3 py-1">
                <Package size={14} className="mr-1" /> Wholesale
              </Badge>
            )}
            {extractor.white_label && (
              <Badge variant="outline" className="px-3 py-1">
                <Tag size={14} className="mr-1" /> White Label
              </Badge>
            )}
          </motion.div>
        )}

        {/* About */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FlaskConical size={18} className="text-purple-500" /> About
              </h2>
              <p className="text-muted-foreground leading-relaxed">{extractor.description}</p>
              
              {extractor.products && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium mb-2">Products</h3>
                  <p className="text-sm text-purple-400">{extractor.products}</p>
                </div>
              )}

              {extractor.years_experience && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>{extractor.years_experience}+</strong> years of extraction experience
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact - Only shown if user is logged in */}
        {user ? (
          hasContactInfo && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-card/50 backdrop-blur border-border">
                <CardContent className="p-4 sm:p-6">
                  <h2 className="text-lg font-semibold mb-4">Contact</h2>
                  <div className="space-y-3">
                    {extractor.email && (
                      <a href={`mailto:${extractor.email}`} className="flex items-center gap-3 text-sm hover:text-purple-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                          <Mail size={18} className="text-purple-500" />
                        </div>
                        <span className="break-all">{extractor.email}</span>
                      </a>
                    )}
                    {extractor.phone && (
                      <a href={`tel:${extractor.phone}`} className="flex items-center gap-3 text-sm hover:text-purple-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                          <Phone size={18} className="text-purple-500" />
                        </div>
                        <span>{extractor.phone}</span>
                      </a>
                    )}
                    {extractor.website && (
                      <a href={extractor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-purple-400 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                          <Globe size={18} className="text-purple-500" />
                        </div>
                        <span>Website</span>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        ) : (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-card/50 backdrop-blur border-purple-500/20">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-3">
                  <Lock size={24} className="text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">Sign In to View Contact Info</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Email, phone, and social media links are only visible to registered users.
                </p>
                <Button asChild variant="outline" className="border-purple-500/50">
                  <a href="/login">Sign In</a>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Social Links - Only shown if user is logged in */}
        {user && (extractor.instagram || extractor.facebook) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardContent className="p-4 sm:p-6">
                <h2 className="text-lg font-semibold mb-4">Social Media</h2>
                <div className="grid grid-cols-2 gap-3">
                  {extractor.instagram && (
                    <a
                      href={extractor.instagram.startsWith('http') ? extractor.instagram : `https://instagram.com/${extractor.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                    >
                      <Instagram size={20} className="text-pink-400" />
                      <span className="text-sm font-medium">Instagram</span>
                    </a>
                  )}
                  {extractor.facebook && (
                    <a
                      href={extractor.facebook.startsWith('http') ? extractor.facebook : `https://facebook.com/${extractor.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 transition-all"
                    >
                      <Facebook size={20} className="text-blue-400" />
                      <span className="text-sm font-medium">Facebook</span>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* CTA */}
        {hasContactInfo && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 border-purple-500/20">
              <CardContent className="p-4 sm:p-6 text-center">
                <h3 className="font-semibold mb-2">Interested in their products?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Reach out directly to discuss wholesale orders or white label services
                </p>
                <a href={`mailto:${extractor.email}?subject=Inquiry from Twin Tube Canna-Club`}>
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    <Mail size={16} className="mr-2" />
                    Contact Extractor
                  </Button>
                </a>
              </CardContent>
            </Card>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default ExtractorProfile;
